import * as THREE from 'three';
import { createGlowTexture } from '../utils/createGlowTexture.js';

/**
 * Birth Star - Zeta Tucanae.
 *
 * The closest cataloged star to exactly 28 light-years from Earth. By
 * coincidence, it is a G2 yellow-white main-sequence star - almost
 * identical to our own Sun. Megha's birth star is essentially a Sun twin.
 *
 * The visible color is pale sun-yellow (not the warm gold of Sirius/Jimmy)
 * to honor the fact that this is a real Sun-class star. The pulse is slower
 * and quieter than Sirius's, giving it a more contemplative character.
 */

// Pale sun-yellow glow, distinct from Sirius's warmer gold
const SUN_GLOW = createGlowTexture('255, 245, 195');

export class BirthStar {
  constructor({ starField, onClick }) {
    this.id = 'birth-star';
    this.discoveryId = 'birth-star';
    this.category = 'places';
    this.interactable = true;
    this.onClickCallback = onClick;
    this._t = 0;
    this._hovering = false;

    // Find the closest cataloged star to exactly 28 light-years
    const star = starField.findClosestToDistance(28);
    if (!star) {
      console.warn('Birth Star: no cataloged star found near 28 ly');
      return;
    }
    this.starData = star;
    this.distanceLightYears = star.d * 3.262;
    this.starName = star.n || star.bf || 'unnamed';

    const position = starField.getStarPosition(star);

    const material = new THREE.SpriteMaterial({
      map: SUN_GLOW,
      color: 0xfff5c8,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
    });

    this.mesh = new THREE.Sprite(material);
    this.mesh.position.copy(position);
    this.baseScale = 30;
    this.mesh.scale.setScalar(this.baseScale);
    this.mesh.renderOrder = 10;
    this.mesh.userData.star = this;
    this.material = material;
  }

  onHover() {
    document.body.classList.add('star-hover');
    this._hovering = true;
  }

  onUnhover() {
    document.body.classList.remove('star-hover');
    this._hovering = false;
  }

  onClick() {
    if (this.onClickCallback) this.onClickCallback(this);
  }

  /**
   * HTML for this star's card overlay.
   * Content from copy/birth-star.md, augmented with the Sun-twin reveal
   * (which depends on Zeta Tucanae specifically being a Sun-class star).
   */
  cardHTML() {
    return `
      <h2>Your Birth Star</h2>
      <div class="label">${this.starName} - Tucana, the Toucan</div>

      <p>Astronomers have known about this star for a long time. They have measured its temperature. They have measured its color. They have measured the way it drifts through the sky.</p>

      <p>They have also measured something quieter:</p>

      <p>How long it takes for the light from this star to reach this planet.</p>

      <p style="font-style: normal; opacity: 1; font-size: 18px; margin-top: 8px;">Twenty-eight years.</p>

      <hr>

      <p>On April 2, 1998, the photons currently arriving at your eyes left this star and started moving through the dark toward Earth. They have been traveling at the speed of light for your entire life.</p>

      <p>Tonight, they finished the journey.</p>

      <p>They came to find you.</p>

      <hr>

      <p>And one more thing about this star.</p>

      <p>It is a G2 yellow-white main-sequence star. Almost identical in size, in color, in temperature, in age to our own Sun.</p>

      <p>You were born under another sun.</p>

      <p>Tonight, that sun's light is finally here.</p>

      <hr>

      <p>You and your birth star are the same age. You always have been. The two of you were made on the same day, and you have both been traveling toward each other ever since.</p>

      <p>Tonight, you finally arrived in the same place.</p>
    `;
  }

  update(dt) {
    this._t += dt;
    // Slower, gentler pulse than Sirius - Birth Star is quieter and more contemplative
    const pulse = 1 + Math.sin(this._t * 0.6) * 0.04;
    const targetScale = this._hovering ? this.baseScale * 1.25 : this.baseScale * pulse;
    const cur = this.mesh.scale.x;
    const next = cur + (targetScale - cur) * 0.18;
    this.mesh.scale.setScalar(next);
  }
}
